/*====================================================
  Header sign-in state
  The guides are a separate app (Ghost) but are served under gotixi.com, so the browser already sends the GoTixi session
  cookie (gotixi_session) here and the API is reachable at /api. This is a plain-JS port of the website navbar's sign-in state:
    apps/web/src/lib/navUser.ts            (what the browser remembers)
    apps/web/src/lib/auth.ts               (/me, sign-out)
    apps/web/src/components/auth/NavAuth.tsx  (the pill and its menu)
  Keep the storage key and shape identical to the website's and change both sides together. The cookie is HttpOnly, so the
  only way to know who is signed in is to ask /api/v1/auth/me; the remembered user is a hint for the header, never an authority.
====================================================*/
(function () {
    var holder = document.getElementById('gt-auth');
    if (!holder) return;

    /* The server adds ?signin=google when it sends a Google sign-in back (the website's Layout.astro handles it the same way): count it
       once in analytics (GTM is on this page too), then remove it from the address so it does not stay in the URL or get carried into
       the "Sign in" link's returnTo. */
    (function () {
        var url = new URL(location.href);
        if (url.searchParams.get('signin') !== 'google') return;
        (window.dataLayer = window.dataLayer || []).push({ event: 'signin_complete_click_google', page: location.pathname });
        url.searchParams.delete('signin');
        history.replaceState(null, '', url.pathname + url.search + url.hash);
    })();

    var NAV_USER_KEY = 'gotixi-nav-user';
    /* Within this long of the last server answer the header does not ask again. */
    var NAV_USER_FRESH_MS = 5 * 60 * 1000;
    /* A memory older than a session can last (14 days) is dropped, whatever the server says (or fails to say). */
    var NAV_USER_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
    var NAV_SIGNED_IN_CLASS = 'nav-signed-in';

    var SITE = (holder.getAttribute('data-site') || '').replace(/\/$/, '');
    var API = (holder.getAttribute('data-api') || '/api').replace(/\/$/, '') + '/v1/auth';
    var SIGN_UP = SITE + '/journey/signup';
    var PROFILE = SITE + '/profile';
    var ADMIN = SITE + '/admin';

    /*----------------------------------------------------
      What the browser remembers (navUser.ts)
    ----------------------------------------------------*/
    function browserStore() {
        try {
            return window.localStorage;
        } catch (e) {
            return null;
        }
    }

    /* By code point, so an emoji is never cut in half; control characters are dropped. */
    function clean(text) {
        return Array.from(text).filter(function (ch) {
            var code = ch.codePointAt(0) || 0;
            return code > 0x1f && code !== 0x7f;
        }).join('');
    }

    function loginId(u) {
        return u.email || u.phone || '';
    }

    /* The name shown in the pill: the first word of the name, else the part of the email before the @ ("Traveller" for a phone-only user). */
    function firstName(u) {
        var word = u.name && u.name.trim().split(/\s+/)[0];
        var local = u.email && u.email.split('@')[0];
        return Array.from(clean(word || local || 'Traveller')).slice(0, 20).join('');
    }

    function initialOf(first) {
        return (Array.from(first)[0] || '').toUpperCase();
    }

    function displayName(u) {
        return (u.name && u.name.trim()) || (u.email && u.email.split('@')[0]) || 'Traveller';
    }

    /* The remembered user, however old (up to 14 days); `fresh` says whether the server was asked less than 5 minutes ago. */
    function readNavUser() {
        try {
            var saved = JSON.parse(browserStore().getItem(NAV_USER_KEY) || 'null');
            var user = saved && saved.user;
            if (!user) return null;
            var text = function (v) { return v === undefined || typeof v === 'string'; };
            if (!text(user.email) || !text(user.phone) || !(user.email || user.phone)) return null;
            if (user.name !== undefined && typeof user.name !== 'string') return null;
            if (typeof saved.at !== 'number') return null;
            var age = Date.now() - saved.at;
            if (age > NAV_USER_MAX_AGE_MS) return null;
            return { user: user, fresh: age >= 0 && age < NAV_USER_FRESH_MS };
        } catch (e) {
            return null;
        }
    }

    /* Remember the user the server just confirmed, or forget (null: signed out). The first name and initial are stored ready to use,
       so the script in default.hbs (which runs before anything else) only has to read them. */
    function saveNavUser(user) {
        try {
            var store = browserStore();
            if (user) {
                var first = firstName(user);
                var saved = { email: user.email, phone: user.phone, name: user.name };
                if (user.isAdmin === true) {
                    saved.isAdmin = true;
                    saved.isSuperAdmin = user.isSuperAdmin === true;
                }
                store.setItem(NAV_USER_KEY, JSON.stringify({ user: saved, at: Date.now(), first: first, initial: initialOf(first) }));
            } else {
                store.removeItem(NAV_USER_KEY);
            }
        } catch (e) {
            /* nothing to remember in */
        }
    }

    /*----------------------------------------------------
      Asking the server (auth.ts)
    ----------------------------------------------------*/
    /* Counts sign-outs. A /me answer that was asked before a sign-out is out of date: it is not remembered and counts as signed out,
       so a slow answer cannot put the user back in the header after they signed out. */
    var signOuts = 0;

    /* null = signed out, or sign-in is off on this server (503). */
    function loadUser() {
        var asked = signOuts;
        return fetch(API + '/me', { credentials: 'include' }).then(function (res) {
            if (res.status === 401 || res.status === 503) {
                if (asked === signOuts) saveNavUser(null);
                return null;
            }
            if (!res.ok) throw new Error('Could not ask who is signed in (' + res.status + ')');
            return res.json().then(function (user) {
                if (asked !== signOuts) return null;
                saveNavUser(user); // the header remembers what the server last said
                return user;
            });
        });
    }

    /* The answer comes from the browser's memory when the server was asked less than 5 minutes ago, else from /me. */
    function navUser() {
        var saved = readNavUser();
        if (saved && saved.fresh) return Promise.resolve(saved.user);
        return loadUser().catch(function () {
            return saved ? saved.user : null; // the server could not be reached: keep what the header was showing
        });
    }

    /* Same steps as the website's signOutEverywhere: the server clears the session cookie, then this browser forgets the user
       (which also tells the other open tabs, through the storage event). Failing leaves the user signed in. */
    function signOut() {
        signOuts++; // answers still on their way from before this are out of date
        fetch(API + '/logout', { method: 'POST', credentials: 'include' }).then(function (res) {
            if (!res.ok) throw new Error('Sign-out failed (' + res.status + ')');
        }).then(function () {
            signOuts++;
            saveNavUser(null);
            setMe(null);
        }, function () {
            signOuts--; // still signed in
        });
    }

    /*----------------------------------------------------
      The pill and its menu (NavAuth.tsx)
    ----------------------------------------------------*/
    var CHEVRON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="transition"><path d="m6 9 6 6 6-6"/></svg>';
    /* Beside the burger on a phone the pill is just the initial, as on the website (NavAuth's "mobile" variant). */
    var PILL = 'flex h-[44px] cursor-pointer items-center gap-[8px] rounded-full border-0 bg-transparent pl-[6px] pr-[12px] text-[16px] font-medium text-text-heading transition-colors hover:bg-secondary-100 night:text-text-night-heading night:hover:bg-surface-night-card [font-family:inherit]';
    var PILL_MOBILE = 'flex h-[40px] cursor-pointer items-center rounded-full border-0 bg-transparent p-[4px] text-[16px] font-medium text-text-heading transition-colors hover:bg-secondary-100 night:text-text-night-heading night:hover:bg-surface-night-card [font-family:inherit]';
    var AVATAR = 'flex items-center justify-center rounded-full bg-brand-forest font-semibold text-text-inverse night:bg-brand-orange h-[36px] w-[36px] text-[14px]';
    var AVATAR_MOBILE = 'flex items-center justify-center rounded-full bg-brand-forest font-semibold text-text-inverse night:bg-brand-orange h-[28px] w-[28px] text-[12px]';
    var MENU = 'absolute right-0 top-full z-[70] mt-[8px] hidden w-[240px] rounded-[16px] border border-solid border-brand-border bg-white p-[8px] text-left shadow-[0px_3px_50px_rgba(0,0,0,0.07)] night:border-border-night night:bg-surface-night-card';
    var MENU_ITEM = 'flex h-[44px] w-full cursor-pointer items-center rounded-[8px] border-0 bg-transparent px-[12px] text-left text-[14px] font-normal text-text-heading no-underline transition hover:bg-secondary-100 night:text-text-night-heading night:hover:bg-surface-night-accent [font-family:inherit]';

    function el(tag, cls, text) {
        var node = document.createElement(tag);
        if (cls) node.className = cls;
        if (text) node.textContent = text;
        return node;
    }

    function link(href, label) {
        var a = el('a', MENU_ITEM, label);
        a.href = href;
        a.setAttribute('role', 'menuitem');
        return a;
    }

    function signOutButton(cls, role) {
        var b = el('button', cls, 'Sign out');
        b.type = 'button';
        if (role) b.setAttribute('role', role);
        b.addEventListener('click', signOut);
        return b;
    }

    /* The profile pill with its menu: next to the nav on desktop, beside the burger on a phone. */
    function pill(me, mobile) {
        var wrap = el('div', 'relative');
        var button = el('button', mobile ? PILL_MOBILE : PILL);
        button.type = 'button';
        button.setAttribute('aria-haspopup', 'menu');
        button.setAttribute('aria-label', 'Account menu for ' + displayName(me));
        button.setAttribute('aria-expanded', 'false');
        button.appendChild(el('span', mobile ? AVATAR_MOBILE : AVATAR, initialOf(firstName(me))));
        if (!mobile) {
            button.appendChild(el('span', 'max-w-[88px] truncate', firstName(me)));
            button.insertAdjacentHTML('beforeend', CHEVRON);
        }

        var menu = el('div', MENU);
        menu.setAttribute('role', 'menu');
        menu.setAttribute('data-gt-menu', '');
        var who = el('div', 'px-[12px] py-[8px]');
        who.appendChild(el('p', 'm-0 truncate text-[14px] font-semibold text-text-heading night:text-text-night-heading', displayName(me)));
        who.appendChild(el('p', 'm-0 truncate text-[12px] text-text-subtle night:text-text-night-subtle', loginId(me)));
        menu.appendChild(who);
        menu.appendChild(el('div', 'my-[4px] h-px bg-brand-border night:bg-border-night'));
        menu.appendChild(link(PROFILE, 'Your account'));
        if (me.isAdmin) menu.appendChild(link(ADMIN, 'Admin Panel'));
        menu.appendChild(signOutButton(MENU_ITEM, 'menuitem'));

        button.addEventListener('click', function () {
            setOpen(!open);
        });
        wrap.appendChild(button);
        wrap.appendChild(menu);
        return wrap;
    }

    /* The mobile drawer shows who is signed in right in the list, no menu to open. */
    function drawerBlock(me) {
        var wrap = el('div', 'mx-auto mt-[16px] flex w-[200px] flex-col items-center gap-[12px] text-center');
        wrap.appendChild(el('span', 'flex h-[44px] w-[44px] items-center justify-center rounded-full bg-brand-forest text-[16px] font-semibold text-text-inverse night:bg-brand-orange', initialOf(firstName(me))));
        var who = el('div', 'min-w-0 max-w-full');
        who.appendChild(el('p', 'm-0 truncate text-[16px] font-semibold text-text-heading night:text-text-night-heading', displayName(me)));
        who.appendChild(el('p', 'm-0 truncate text-[12px] text-text-subtle night:text-text-night-subtle', loginId(me)));
        wrap.appendChild(who);
        wrap.appendChild(link(PROFILE, 'Your account'));
        if (me.isAdmin) wrap.appendChild(link(ADMIN, 'Admin Panel'));
        wrap.appendChild(signOutButton('h-[44px] w-full cursor-pointer rounded-[14px] border border-solid border-brand-border bg-transparent text-[16px] font-medium text-text-heading transition hover:bg-secondary-100 night:border-border-night night:text-text-night-heading night:hover:bg-surface-night-accent [font-family:inherit]'));
        return wrap;
    }

    /*----------------------------------------------------
      Showing it
    ----------------------------------------------------*/
    var current; // undefined = still asking, null = signed out, else the user
    var open = false;
    var mounts = Array.prototype.map.call(document.querySelectorAll('[data-gt-auth]'), function (node) {
        return { node: node, variant: node.getAttribute('data-gt-auth'), guest: node.innerHTML, signedIn: false };
    });

    /* "Sign in" sends people to the website's sign-in page and brings them back to this page afterwards. A first-time user does the
       onboarding on the way: `/journey/from?next=` is the sign-in's "onboarding first, then here" (same as the website's signInHref). */
    function setSignInLinks() {
        var page = location.pathname + location.search + location.hash;
        var back = encodeURIComponent('/journey/from?next=' + encodeURIComponent(page));
        Array.prototype.forEach.call(document.querySelectorAll('[data-gt-signin]'), function (a) {
            a.href = SIGN_UP + '?returnTo=' + back;
        });
    }

    function setOpen(next) {
        open = next;
        Array.prototype.forEach.call(document.querySelectorAll('[data-gt-menu]'), function (menu) {
            menu.classList.toggle('hidden', !open);
            var button = menu.previousSibling;
            button.setAttribute('aria-expanded', open ? 'true' : 'false');
            var chevron = button.querySelector('svg');
            if (chevron) chevron.classList.toggle('rotate-180', open);
        });
    }

    function render() {
        if (current === undefined) return;
        mounts.forEach(function (m) {
            if (current) {
                m.node.textContent = '';
                m.node.appendChild(m.variant === 'drawer' ? drawerBlock(current) : pill(current, m.variant === 'mobile'));
                m.signedIn = true;
            } else if (m.signedIn) {
                m.node.innerHTML = m.guest; // the signed-out markup the server sent
                m.signedIn = false;
            }
        });
        setSignInLinks();
        open = false;
        // <html> says "signed in" from before the first paint (default.hbs) so the guest buttons stay hidden; keep it true to what we show.
        document.documentElement.classList.toggle(NAV_SIGNED_IN_CLASS, !!current);
    }

    function idOf(u) {
        return u ? loginId(u) : undefined;
    }

    function setMe(user) {
        // The server confirming what is already shown changes nothing, so an open menu stays open.
        if (current && user && JSON.stringify(current) === JSON.stringify(user)) return;
        current = user;
        render();
    }

    /* First thing: show the user remembered from the last visit (the server HTML cannot know who is signed in). */
    var remembered = readNavUser();
    if (remembered) setMe(remembered.user);
    else setSignInLinks();

    /* Tells the head script in default.hbs that this script loaded, so it keeps the guest buttons hidden. */
    document.documentElement.setAttribute('data-nav-ready', '');

    navUser().then(setMe);

    /* Another tab changed the remembered user. A different person, or the same one renamed: show them. Forgotten (a sign-out there, or a new sign-in):
       ask the server before switching. A refresh of the same user changes nothing, so an open menu stays open. */
    window.addEventListener('storage', function (e) {
        if (e.key !== NAV_USER_KEY) return;
        var saved = readNavUser();
        var next = saved && saved.user;
        if (next) {
            if (idOf(next) === idOf(current) && displayName(next) === displayName(current)) return;
            setMe(next);
        } else {
            loadUser().then(function (u) {
                if (idOf(u) === idOf(current)) return;
                setMe(u);
            }).catch(function () {});
        }
    });

    window.addEventListener('hashchange', setSignInLinks);

    document.addEventListener('mousedown', function (e) {
        if (open && !e.target.closest('[data-gt-auth]')) setOpen(false);
    });
    document.addEventListener('keydown', function (e) {
        if (open && e.key === 'Escape') setOpen(false);
    });
})();
